import React from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../Theme/ThemeProvider';

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  showCancelButton?: boolean;
  confirmButtonColor?: string;
  confirmButtonHoverColor?: string;
  confirmIcon?: React.ReactNode;
}

export function ConfirmationDialog({ 
  isOpen, 
  title, 
  message, 
  onConfirm, 
  onCancel,
  confirmText = 'Yes, Clear Chat',
  cancelText = 'Cancel',
  showCancelButton = true,
  confirmButtonColor = '#5E95ED',
  confirmButtonHoverColor = '#4a82d8',
  confirmIcon,
}: ConfirmationDialogProps) {
  const { mode } = useTheme();

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 30000 }}
      onClick={onCancel}
    >
      <div 
        className="rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
        style={{
          backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
          border: `1px solid ${mode === 'dark' ? '#333' : '#e5e5e5'}`
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title */}
        <h2 
          className="text-lg font-semibold mb-3"
          style={{ 
            color: 'var(--color-text)',
            fontFamily: '3MCircularTT'
          }}
        >
          {title}
        </h2>

        {/* Message */}
        <p 
          className="text-sm mb-6"
          style={{ 
            color: 'var(--color-text)',
            fontFamily: '3MCircularTT',
            opacity: 0.9
          }}
        >
          {message}
        </p>

        {/* Buttons */}
        <div className="flex justify-end gap-3">
          {showCancelButton && (
            <button
              onClick={onCancel}
              className="px-4 py-2 rounded text-sm font-medium transition-colors"
              style={{
                backgroundColor: mode === 'dark' ? '#333' : '#f3f4f6',
                color: 'var(--color-text)',
                fontFamily: '3MCircularTT',
                border: 'none',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = mode === 'dark' ? '#444' : '#e5e7eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = mode === 'dark' ? '#333' : '#f3f4f6';
              }}
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded text-sm font-medium transition-colors inline-flex items-center gap-1.5"
            style={{
              backgroundColor: confirmButtonColor,
              color: '#ffffff',
              fontFamily: '3MCircularTT',
              border: 'none',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = confirmButtonHoverColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = confirmButtonColor;
            }}
          >
            {confirmIcon}
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
